import {IntlShape} from 'react-intl';
import * as Sentry from '@sentry/browser';
import DirectApi from '../../../directApi';
import {
	ExportQuickbooksInvoiceParams,
	InvoiceEntryRow,
	QuickbookProductService,
	QuickbookSetting,
	QuickbookSettingsMutationInput,
	QuickbooksSettingId,
	TaxCode,
} from './ExportInvoiceModal.type';
import Util from '../../../forecast-app/shared/util/util';
import CreateQuickbooksInvoiceMutation from '../../../mutations/CreateQuickbooksInvoiceMutation';
import {MODAL_TYPE, showModal} from '../../../forecast-app/shared/components/modals/generic_modal_conductor';
import {createToast} from '../../../forecast-app/shared/components/toasts/another-toast/toaster';
import {BUTTON_COLOR, BUTTON_STYLE} from '../../../constants';
import {
	QBO_EXCLUSIVE_PAIRS,
	StoredQboExportDefaults,
	toQboExportDefaults,
} from '../../../forecast-app/admin-tab/integrations-page/export-defaults/qboExportDefaults';

/** Each option's starting value, keyed by the window's own setting ids. */
export type QuickbookSettingDefaults = Record<QuickbooksSettingId, boolean>;

/*
 * The company's export defaults (T4-148) as the window's starting values (T4-150). An option no Admin
 * has saved starts off. A stored pair with descriptions and project name both on - possible, because two
 * single-field saves are not locked against each other (spec 013) - opens as descriptions only:
 * quickbooks-online ignores the project name while descriptions are on (postInvoice.ts:211-217), so
 * QuickBooks receives the same invoice, and the window starts inside its own rule for the pair.
 */
export const resolveQuickbookSettingDefaults = (
	company: Partial<StoredQboExportDefaults> | null | undefined
): QuickbookSettingDefaults => {
	const defaults = toQboExportDefaults(company);
	// The first field of each pair wins: QuickBooks ignores the project name while descriptions are on
	// (postInvoice.ts:211-217), so the invoice is unchanged.
	for (const [kept, dropped] of QBO_EXCLUSIVE_PAIRS) {
		if (defaults[kept] && defaults[dropped]) {
			defaults[dropped] = false;
		}
	}
	return {
		[QuickbooksSettingId.CONTROL_INVOICE_REFERENCE]: defaults.qboExportDefaultInvoiceRef,
		[QuickbooksSettingId.USE_PRODUCT_SERVICE_DESCRIPTIONS]: defaults.qboExportDefaultDescription,
		[QuickbooksSettingId.INCLUDE_PROJECT_NAME_IN_DESCRIPTION]: defaults.qboExportDefaultProjectNameInDesc,
		[QuickbooksSettingId.USE_QUICKBOOKS_TERMS]: defaults.qboExportDefaultTerms,
		[QuickbooksSettingId.USE_CUSTOMER_DEFAULT_TAX]: defaults.qboExportDefaultTax,
	};
};

// Without `defaults` every option starts off, as before T4-150.
export const getInitialQuickbookSettings = (intl: IntlShape, defaults?: QuickbookSettingDefaults): QuickbookSetting[] => {
	const {formatMessage} = intl;
	const checked = (id: QuickbooksSettingId): boolean => defaults?.[id] ?? false;

	return [
		{
			id: QuickbooksSettingId.CONTROL_INVOICE_REFERENCE,
			name: formatMessage({id: 'invoicing.quickbooks.control_reference'}),
			checked: checked(QuickbooksSettingId.CONTROL_INVOICE_REFERENCE),
			disabled: false,
		},
		{
			id: QuickbooksSettingId.USE_PRODUCT_SERVICE_DESCRIPTIONS,
			name: formatMessage({id: 'invoicing.quickbooks.use_product_service_descriptions'}),
			checked: checked(QuickbooksSettingId.USE_PRODUCT_SERVICE_DESCRIPTIONS),
			disabled: false,
		},
		{
			id: QuickbooksSettingId.INCLUDE_PROJECT_NAME_IN_DESCRIPTION,
			name: formatMessage({id: 'invoicing.quickbooks.include_project_name_in_description_when_exporting'}),
			checked: checked(QuickbooksSettingId.INCLUDE_PROJECT_NAME_IN_DESCRIPTION),
			disabled: false,
		},
		{
			id: QuickbooksSettingId.USE_QUICKBOOKS_TERMS,
			name: formatMessage({id: 'invoicing.quickbooks.use_terms'}),
			checked: checked(QuickbooksSettingId.USE_QUICKBOOKS_TERMS),
			disabled: false,
		},
		{
			id: QuickbooksSettingId.USE_CUSTOMER_DEFAULT_TAX,
			name: formatMessage({id: 'invoicing.quickbooks.use_default_tax'}),
			checked: checked(QuickbooksSettingId.USE_CUSTOMER_DEFAULT_TAX),
			disabled: false,
		},
	];
};

// The legacy modal fetched with no realm when the client carried none; the service then uses the
// company's single connection. An empty realm would request "tax_codes/" instead.
const realmPath = (realmId: string | null): string => (realmId ? `/${realmId}` : '');

const getQuickbooksTaxCodes = async (realmId: string | null): Promise<TaxCode[]> => {
	return DirectApi.Fetch(`quickbooks/tax_codes${realmPath(realmId)}`).then(json => {
		return json.map(taxCode => ({
			value: taxCode.id,
			label: taxCode.name,
			taxRate: taxCode.tax,
		}));
	});
};

const getProductsServices = async (realmId: string | null): Promise<QuickbookProductService[]> => {
	return DirectApi.Fetch(`quickbooks/items_and_services${realmPath(realmId)}`).then(json => {
		return json.map(item => ({
			value: item.id,
			label: item.name,
			description: item.description,
			unitPrice: item.unitPrice,
			taxable: item.taxable,
		}));
	});
};

/** Which of the two lists failed to load. An empty list that loaded is not a failure. */
export type QuickbooksLoadFailures = {taxCodesFailed: boolean; productsFailed: boolean};

/*
 * The two lists load independently, and `onDone` ALWAYS runs, saying which list failed. The caller
 * holds the global loader until it does, so one failed list - a rejection, or an error body that is
 * not an array - must not leave the page under an overlay only a reload clears. A failed list is
 * still set empty, but the caller is told, so it can say so rather than show an empty list as if
 * the company had nothing in it. Each failure is reported to Sentry. On success the setters run
 * together, in the same order as before.
 */
export const fetchQuickbooksData = (
	setQuickbooksTaxCodes: (taxCodes: TaxCode[]) => void,
	setSelectedQuickbooksTaxCode: (taxCode: TaxCode | null) => void,
	setProductsServices: (productsServices: QuickbookProductService[]) => void,
	onDone: (failures: QuickbooksLoadFailures) => void,
	realmId: string | null
): void => {
	// `getQuickbooksTaxCodes` is async, so a `.map` on a non-array body rejects rather than throws.
	const orNull = <T>(promise: Promise<T>, list: string): Promise<T | null> =>
		promise.catch(error => {
			Sentry.captureException(error, {tags: {quickbooksList: list}, extra: {realmId}});
			return null;
		});

	Promise.all([
		orNull(getQuickbooksTaxCodes(realmId), 'tax_codes'),
		orNull(getProductsServices(realmId), 'items_and_services'),
	]).then(([taxCodes, productsServices]) => {
		setQuickbooksTaxCodes(taxCodes ?? []);
		if (taxCodes) {
			setSelectedQuickbooksTaxCode(taxCodes.find(taxCode => taxCode.value === '-1') || null);
		}
		setProductsServices(productsServices ?? []);

		onDone({taxCodesFailed: taxCodes === null, productsFailed: productsServices === null});
	});
};

const onExportInvoiceSuccess = (result, intl: IntlShape, onSuccess: () => void): void => {
	const {formatMessage} = intl;

	onSuccess();

	const {createQuickbooksInvoice} = result || {};
	if (createQuickbooksInvoice) {
		const {success, errorMessage} = createQuickbooksInvoice;

		if (success) {
			createToast({duration: 5000, message: formatMessage({id: 'integrations.quickbooks.invoice_created'})});
		} else {
			showModal({
				type: MODAL_TYPE.WARNING,
				warningMessageId: 'invoicing.not_exported',
				warningInformation: [errorMessage || 'The invoice could not be exported.'],
				buttons: [
					{
						text: formatMessage({id: 'common.ok'}),
						style: BUTTON_STYLE.FILLED,
						color: BUTTON_COLOR.WHITE,
					},
				],
			});
		}
	}
};

const getQuickbookSettingsInput = (settings: QuickbookSetting[]): QuickbookSettingsMutationInput => {
	const settingsInput = {
		letQBOSetDescription: false,
		letQBOSetInvoiceRef: false,
		useQBOTerms: false,
		useQBODefaultTax: false,
		includeProjectNameInDesc: false,
	};

	settings.forEach(setting => {
		switch (setting.id) {
			case QuickbooksSettingId.USE_PRODUCT_SERVICE_DESCRIPTIONS:
				settingsInput.letQBOSetDescription = setting.checked;
				break;
			case QuickbooksSettingId.CONTROL_INVOICE_REFERENCE:
				settingsInput.letQBOSetInvoiceRef = setting.checked;
				break;
			case QuickbooksSettingId.USE_QUICKBOOKS_TERMS:
				settingsInput.useQBOTerms = setting.checked;
				break;
			case QuickbooksSettingId.USE_CUSTOMER_DEFAULT_TAX:
				settingsInput.useQBODefaultTax = setting.checked;
				break;
			case QuickbooksSettingId.INCLUDE_PROJECT_NAME_IN_DESCRIPTION:
				settingsInput.includeProjectNameInDesc = setting.checked;
				break;
			default:
				break;
		}
	});

	return settingsInput;
};

type QuickbooksInputParams = Pick<
	ExportQuickbooksInvoiceParams,
	'target' | 'invoice' | 'invoiceRows' | 'quickbookSettings' | 'taxCode'
>;

export const buildQuickbooksInvoiceInput = ({
	target,
	invoice,
	invoiceRows,
	quickbookSettings,
	taxCode,
}: QuickbooksInputParams) => {
	const entryMap = invoiceRows.map(row => ({entryId: row.id, itemId: row.quickbooksItem}));

	if (target.kind === 'CLIENT') {
		// Exactly what quickbooks_export_modal.js sends, so the service resolves customer and realm
		// from the client's own refs - the same request as today.
		return {
			invoiceId: invoice.id,
			forecastClientId: target.clientId,
			entryMap,
			taxCodeId: taxCode?.value ?? '-1',
			...getQuickbookSettingsInput(quickbookSettings),
		};
	}

	const {billTo} = target;
	return {
		invoiceId: invoice.id,
		billToId: billTo.id,
		quickbooksClientId: billTo?.externalId,
		// '-1' ("No tax") when the codes failed to load, as the client branch and the window do.
		taxCodeId: taxCode?.value ?? '-1',
		entryMap,
		realmId: billTo.billFrom?.externalId,
		...getQuickbookSettingsInput(quickbookSettings),
	};
};

export const exportInvoiceToQuickbooksOnline = ({intl, onSuccess, onError, ...params}: ExportQuickbooksInvoiceParams): void => {
	Util.CommitMutation(
		CreateQuickbooksInvoiceMutation,
		buildQuickbooksInvoiceInput(params),
		result => onExportInvoiceSuccess(result, intl, onSuccess),
		true,
		null,
		onError
	);
};

/*
 * "Use QuickBooks product/service descriptions" and "Include project name in description" cannot
 * both be on - the legacy window enforces it (quickbooks_export_modal.js:77-99). Turning one on
 * turns the other off; turning one off touches nothing else.
 */
const MUTUALLY_EXCLUSIVE: Partial<Record<QuickbooksSettingId, QuickbooksSettingId>> = {
	[QuickbooksSettingId.USE_PRODUCT_SERVICE_DESCRIPTIONS]: QuickbooksSettingId.INCLUDE_PROJECT_NAME_IN_DESCRIPTION,
	[QuickbooksSettingId.INCLUDE_PROJECT_NAME_IN_DESCRIPTION]: QuickbooksSettingId.USE_PRODUCT_SERVICE_DESCRIPTIONS,
};

export const toggleQuickbookSetting = (
	settings: QuickbookSetting[],
	id: QuickbooksSettingId,
	enforceRules: boolean
): QuickbookSetting[] => {
	const toggled = settings.map(item => (item.id === id ? {...item, checked: !item.checked} : item));
	const changed = toggled.find(item => item.id === id);
	const other = changed?.checked && enforceRules ? MUTUALLY_EXCLUSIVE[changed.id] : undefined;
	return other === undefined ? toggled : toggled.map(item => (item.id === other ? {...item, checked: false} : item));
};

const isOn = (settings: QuickbookSetting[], id: QuickbooksSettingId): boolean =>
	settings.some(setting => setting.id === id && setting.checked);

/** What QuickBooks will receive as this line's description - mirrors postInvoice.ts:170-183. */
export const getQuickbooksLineDescription = (row: InvoiceEntryRow, settings: QuickbookSetting[]): string => {
	const useQuickbooks = isOn(settings, QuickbooksSettingId.USE_PRODUCT_SERVICE_DESCRIPTIONS);
	if (useQuickbooks && row.quickbooksDescription) {
		return row.quickbooksDescription;
	}

	const description = row.description ?? '';
	const projectName = row.project?.name;
	if (
		!useQuickbooks &&
		isOn(settings, QuickbooksSettingId.INCLUDE_PROJECT_NAME_IN_DESCRIPTION) &&
		projectName &&
		!description.includes(projectName)
	) {
		return `${projectName}: ${description}`;
	}

	return description;
};
